"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The "couldn't connect / try again" popup above the launcher. It opens when the visitor taps the
 * launcher while auth is failing and closes when the error clears.
 *
 * Tapping Try again closes it (the launcher spinner takes over as the working indicator). If that
 * retry ends in an error AGAIN the popup comes back by itself: otherwise the spinner just turned
 * back into a plain launcher and the visitor had to tap again to learn it still fails.
 */
export function useAuthPopup({
  hasError,
  hasSession,
  onRetry,
}: {
  /** A handshake error is currently showing. */
  hasError: boolean;
  /** A session (token) exists: a retry that got here succeeded. */
  hasSession: boolean;
  /** Re-fire the handshake. */
  onRetry: () => void;
}) {
  const [open, setOpen] = useState(false);
  const retryPending = useRef(false);

  useEffect(() => {
    if (!hasError) setOpen(false);
  }, [hasError]);

  useEffect(() => {
    if (hasSession) retryPending.current = false;
  }, [hasSession]);

  // The retry finished with an error: show it again.
  useEffect(() => {
    if (hasError && retryPending.current) {
      retryPending.current = false;
      setOpen(true);
    }
  }, [hasError]);

  const retry = useCallback(() => {
    retryPending.current = true;
    setOpen(false);
    onRetry();
  }, [onRetry]);
  const show = useCallback(() => setOpen(true), []);
  const dismiss = useCallback(() => setOpen(false), []);
  return { open, show, dismiss, retry };
}
